import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import {
  Container,
  ImageContainer,
  ImageOverlayContainer,
  SingleDateVariant,
} from './Common'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import GeoContext from 'contexts/geoContext'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience } from 'tripPlanner/hooks/api'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ExperienceItem } from 'tripPlanner/types/tripItem'
import { experiencePageUrlWithPrefilledState } from 'tripPlanner/utils'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

interface Props {
  item: ExperienceItem
}

interface ExperienceProps extends Props {
  experience: App.ExperienceOffer
}

const Image = styled.img`
  object-fit: cover;
  object-position: center;
  height: 100%;
  width: 100%;
`

function LuxExperience({ experience, item }: ExperienceProps) {
  const { place, isBooked, name, type, startDate, startTime, startDay } = item
  const { region } = place

  const openModal = useOpenModal()
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const experienceUrl = experiencePageUrlWithPrefilledState(
    experience,
    item.savedItemData,
  )

  const imgUrl = experience.images[0]?.url
  const imgId = experience.images[0]?.id

  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  const handleClick = useCallback(() => {
    if (isBooked) {
      openModal(TripItemSummary, { item })
    }
    if (canEdit) {
      setImmersiveMode({
        tripId: item.tripId,
        sectionId: itemSectionId(item.id),
      })
    }
  }, [canEdit, isBooked, item, openModal, setImmersiveMode])

  return (
    <>
      {experience.images[0] && (
        <ImageContainer>
          {imgUrl && <Image src={imgUrl} />}
          {!imgUrl && imgId && <ResponsiveImage id={imgId} />}
          <ImageOverlayContainer>
            {experience.leExclusive && experience.productType && (
              <ProductTypeLabel productType={experience.productType} />
            )}
          </ImageOverlayContainer>
        </ImageContainer>
      )}
      <SingleDateVariant
        region={region}
        name={name}
        date={startDate}
        time={startTime}
        type={type}
        isBooked={isBooked}
        to={isBooked ? undefined : experienceUrl}
        onClick={handleClick}
        linksToOffer={!isBooked}
        canEdit={canEdit && !isBooked}
        day={startDay}
      />
    </>
  )
}

const ExperienceSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ item }, ref) => {
    const { code } = item

    const { currentCurrency, currentRegionCode } = useContext(GeoContext)
    const { data: experience, isLoading } = useExperience({
      experienceId: code,
      currentCurrency,
      currentRegionCode,
    })

    return (
      <Container ref={ref}>
        {experience && <LuxExperience experience={experience} item={item} />}
        {isLoading && <LoadingIndicator inline />}
        {!isLoading && !experience && <>Could not load offer.</>}
      </Container>
    )
  },
)

export default ExperienceSummaryCard
